import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Select } from '@/shared/ui/Select';
import { useToast } from '@/shared/ui/Toast';
import { agencyApi, type Location } from '../../api/agencyApi';
import { agencyKeys } from '../../queryKeys';
import styles from './ModuleControls.module.css';

type ReviewMode = 'autopilot' | 'guided' | null;

const MODE_OPTIONS = [
  { value: '', label: 'Disabled' },
  { value: 'guided', label: 'Guided' },
  { value: 'autopilot', label: 'Autopilot' },
];

/**
 * The review responder mode for one location: off, drafts for approval, or
 * posts on its own. Same mutation the Setup tab's location row uses.
 */
export function ReviewModeControl({
  practiceId,
  location,
}: {
  practiceId: string;
  location: Location;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const reviewModeMutation = useMutation({
    mutationFn: (mode: ReviewMode) => agencyApi.setReviewMode(practiceId, location.id, mode),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: agencyKeys.practice(practiceId) });
      toast.success('Review responder updated.');
    },
    onError: () => toast.error('Failed to update review mode.'),
  });

  return (
    <div className={styles.row}>
      <div>
        <div className={styles.label}>Review responder</div>
        <div className={styles.desc}>
          Guided drafts a reply to each new Google review for the practice to approve. Autopilot
          posts the reply itself. Disabled leaves reviews untouched.
        </div>
      </div>
      <div className={styles.control}>
        <div className={styles.selectWrap}>
          <Select
            aria-label="Review responder mode"
            value={location.reviewSettings?.mode ?? ''}
            disabled={reviewModeMutation.isPending}
            onChange={(e) => {
              const val = e.target.value;
              reviewModeMutation.mutate(val === 'autopilot' || val === 'guided' ? val : null);
            }}
            options={MODE_OPTIONS}
            fullWidth
          />
        </div>
      </div>
    </div>
  );
}
